import type { FormLayout } from '../../components/generic';
import { APP_CONFIG, formatDate as formatDateGlobal } from '../../config/appConfig';
import api from '../../services/api';

export const CONDITION_FIELD_OPTIONS = [
  { value: 'from', label: 'From' },
  { value: 'to', label: 'To' },
  { value: 'subject', label: 'Subject' },
  { value: 'body', label: 'Body' },
  { value: 'folder', label: 'Folder' },
] as const;

export const CONDITION_OPERATOR_OPTIONS = [
  { value: 'contains', label: 'Contains' },
  { value: 'equals', label: 'Equals' },
  { value: 'startsWith', label: 'Starts with' },
  { value: 'endsWith', label: 'Ends with' },
] as const;

export const CONDITION_MATCH_OPTIONS = [
  { value: 'all', label: 'All conditions match' },
  { value: 'any', label: 'Any condition matches' },
] as const;

export const ACTION_TYPE_OPTIONS = [
  { value: 'copyToFolder', label: 'Copy to folder' },
  { value: 'moveToJunk', label: 'Move to Junk' },
  { value: 'assignUser', label: 'Assign to user' },
  { value: 'setWorkflowStatus', label: 'Set status' },
  { value: 'markRead', label: 'Mark as read' },
  { value: 'addTag', label: 'Add tag' },
] as const;

export const WORKFLOW_STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'working', label: 'In Progress' },
  { value: 'closed', label: 'Closed' },
] as const;

/** Canonical folder value stored on Email.folder when moving to the account junk mailbox */
export const JUNK_MAILBOX_FOLDER = 'JUNK';

export const moduleConfig = {
  module: {
    title: 'Email Rules',
    description: 'Automate actions on incoming mail after sync',
    i18nKey: 'email-rules',
    permissionModuleKey: 'email_rules',
    route: {
      list: '/email-rules',
      new: '/email-rules/new',
      detail: (id: number) => `/email-rules/${id}`,
      edit: (id: number) => `/email-rules/${id}/edit`,
    },
    api: {
      list: '/email-rules',
      detail: (id: number) => `/email-rules/${id}`,
    },
  },
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },
  detail: {
    subtitle: 'Email rule details',
    notFoundTitle: 'Email rule not found',
    notFoundMessage: 'The email rule you are looking for does not exist or you do not have access.',
    notFoundBackText: 'Back to email rules',
    defaultTitle: 'Email rule',
    getTitle: (item: { name?: string | null; id: number }) => item.name?.trim() || `Rule #${item.id}`,
  },
  emptyStates: {
    noData: 'No email rules yet. Create one to move, tag, or block mail automatically.',
  },
  delete: {
    title: 'Delete email rule',
    message: 'Are you sure you want to delete this rule? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete email rule. Please try again.',
    handler: async (id: number) => {
      await api.delete(`/email-rules/${id}`);
    },
  },
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString);
    },
  },
  form: {
    new: {
      title: 'New email rule',
      subtitle: 'Automate actions on incoming mail after sync',
      submitText: 'Create rule',
    },
    edit: {
      title: 'Edit email rule',
      subtitle: 'Update when this rule runs and what it does',
      submitText: 'Save changes',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading rule...',
    errorMessage: 'Failed to save email rule. Please try again.',
  },
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'basic',
        title: 'Rule details',
        description: 'Name the rule and choose which accounts it applies to.',
        icon: '⚡',
        gridCols: 'md:grid-cols-12',
        fields: ['name', 'emailAccountId', 'priority', 'enabled'],
        fieldSpans: {
          name: 'md:col-span-12',
          emailAccountId: 'md:col-span-6',
          priority: 'md:col-span-3',
          enabled: 'md:col-span-3',
        },
        collapsible: true,
      },
      {
        id: 'when',
        title: 'When',
        description: 'Conditions evaluated after each email sync.',
        icon: '🔍',
        gridCols: 'md:grid-cols-1',
        fields: ['conditions'],
        collapsible: true,
      },
      {
        id: 'then',
        title: 'Then',
        description: 'Actions applied to matching messages.',
        icon: '▶️',
        gridCols: 'md:grid-cols-1',
        fields: ['actions'],
        collapsible: true,
      },
    ],
  } as FormLayout,
};
